import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import {
  Box,
  Typography,
  Theme,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  TextField,
  Tooltip,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { FormProvider, useForm, useWatch, FieldPath } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from 'tss-react/mui';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SaveIcon from '@mui/icons-material/Save';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import FormSubmitButton from '../../components/buttons/FormSubmitButton';
import FormCancelButton from '../../components/buttons/FormCancelButton';
import FormTextField from '../../components/formFields/FormTextField';
import FormMaskedTextField from '../../components/formFields/FormMaskedTextField';
import FormSelectField from '../../components/formFields/FormSelectField';
import FormCheckboxField from '../../components/formFields/FormCheckboxField';
import AddressAutocomplete from '../../components/formFields/AddressAutocomplete';
import SearchVendorPopup from '../../components/SearchVendorPopup';
import VendorConfirmationPopup, { VendorOption } from '../../components/VendorConfirmationPopup';
import FindVendorButton from '../../components/buttons/FindVendorButton';
import { CreateVendorAccountFormSchema } from '../../helpers/formSchemas/formSchemas';
import { VENDOR_ACCOUNT_CREATE_DEFAULT_VALUES } from '../../helpers/formSchemas/formDefaultValues';
import {
  US_STATES_LIST,
  VENDOR_TYPE_OPTIONS,
} from '../../constants/constants';
import { AddressPrediction, validateAddress } from '../../api/addressApi/addressApi';
import { addressOptionsQuery, mapAddressResults, accountingVendorType } from './helpers';
import useSnackBar from '../../hooks/useSnackBar';
import { VendorAccountSubmitFormProps } from './types';
import {
  createVendorAccount,
  getVendorConfig,
  getBankRoutingDetails,
} from '../../api/vendorApi/vendorAccountsApi';
import { AxiosError } from 'axios';
import { ErrorData } from '../../types';
import FormMultiFileUploadField from '../../components/formFields/FormMultiFileUploadField';
import CommonButton from '../../components/buttons/CommonButton';
import FindNetSuiteVendorPopup from '../../components/FindNetSuiteVendorPopup';
import { NetSuiteVendor } from '../../views/VendorsView/types';

declare global {
  interface Window {
    checkCustomAddresses?: () => boolean;
  }
}
type FormValues = {
  name: string;
  email: string;
  tin: string;
  contactName: string;
  participationAddress1: string;
  participationAddress2: string;
  participationCity: string;
  participationState: string;
  participationZip: string;
  externalVendorId: string | null;
  vendorType: string;
};

const useStyles = makeStyles()((theme: Theme) => ({
  vendorCreateForm: {
    padding: '32px',
    flexBasis: '896px',
    backgroundColor: theme.palette.primary.light,
    borderRadius: '16px',
    '&, & *': {
      fontFamily: 'Inter, sans-serif',
    },

    '@media (max-width: 1100px)': {
      flexBasis: 'auto',
    },

    '@media (max-width: 725px)': {
      flexBasis: '520px',
      padding: '16px',
    },
  },
  formFieldSystem: {
    '& .MuiInputLabel-root': {
      fontFamily: 'Inter',
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '120%',
      letterSpacing: '0px',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: `${theme.palette.common.white} !important`,
    },
    '& .MuiOutlinedInput-root': {
      minHeight: '54px',
      borderRadius: '12px',
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Inter',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '120%',
    },
    '& .MuiSelect-select': {
      fontFamily: 'Inter',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '120%',
    },
    '& .MuiFormHelperText-root': {
      fontFamily: 'Inter',
      fontSize: '12px',
      fontWeight: 500,
      lineHeight: '120%',
    },
  },
  formContainer: {
    display: 'flex',
    width: '100%',
    gap: '32px',
    paddingBottom: '20px',

    '@media (max-width: 1100px)': {
      flexDirection: 'column',
    },
  },
  divider: {
    height: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    margin: '0px',
  },
  fieldHeadline: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
  },
  formWrapper: {
    display: 'flex',
  },
  tabsContainer: {
    '&, & *': {
      fontFamily: 'Inter, sans-serif',
    },
    '& .MuiTabs-root': {
      minHeight: 'auto',
    },
    '& .MuiTab-root': {
      color: theme.palette.text.disabled,
      fontSize: '20px',
      fontWeight: 500,
      fontFamily: 'Inter',
      textTransform: 'capitalize',
      minHeight: '48px',
      '&.Mui-selected': {
        color: theme.palette.common.white,
      },
    },
    '& .MuiTabs-indicator': {
      backgroundColor: theme.palette.info.main,
    },
  },
  accordion: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    borderRadius: '10px',
    marginBottom: '16px',
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: '0 0 16px 0',
    },
  },
  accordionSummary: {
    backgroundColor: 'transparent',
    minHeight: '56px',
    '&.Mui-expanded': {
      minHeight: '56px',
    },
    '& .MuiAccordionSummary-content': {
      alignItems: 'center',
      justifyContent: 'space-between',
      margin: 0,
    },
    '& .MuiAccordionSummary-expandIconWrapper': {
      order: -1,
      marginRight: '12px',
    },
  },
  accordionDetails: {
    padding: '16px 24px 24px',
    backgroundColor: 'transparent',
  },
  verifyButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'Inter',
    '&:hover': {
      opacity: 0.8,
    },
    padding: '7px 14px 7px 14px',
    borderRadius: '8px',
    backgroundColor: theme.palette.info.main,
  },
  accountListTitle: {
    color: theme.palette.text.secondary,
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: 500,
  },
  accountCard: {
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '10px',
    padding: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    background: 'rgba(255,255,255,0.02)',
  },
  accountTitle: {
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '20px',
  },
  accountText: {
    color: theme.palette.text.secondary,
    fontFamily: 'Inter',
    fontSize: '13px',
    lineHeight: '18px',
  },
  defaultPill: {
    color: theme.palette.success.main,
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: 600,
  },
  accountActions: {
    display: 'flex',
    gap: '12px',
    marginTop: '4px',
  },
  checkboxWrapper: {
    width: '100%',
    '& label': {
      marginRight: 0,
    },
    '& .MuiCheckbox-root': {
      color: '#5e6174',
      padding: '4px',
      '&.Mui-checked': {
        color: '#2DD4BF',
      },
    },
    '& .MuiFormControlLabel-label': {
      color: theme.palette.text.secondary,
      fontFamily: 'Inter',
      fontSize: '16px',
      fontWeight: 500,
    },
  },
  matchingTextField: {
    width: '100%',
    background: 'rgba(255, 255, 255, 0.03)',
    borderRadius: '12px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.secondary.dark,
        borderRadius: '12px',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.info.main,
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderWidth: '2px',
        borderColor: theme.palette.info.main,
      },
    },
    '& .MuiOutlinedInput-input': {
      padding: '13.5px 16px',
      color: theme.palette.common.white,
      fontFamily: 'Inter',
      fontSize: '14px',
      lineHeight: '120%',
      fontWeight: 500,
    },
    '& .MuiInputLabel-root': {
      color: theme.palette.text.disabled,
      '&[data-shrink="false"]': {
        top: '-3px',
      },
      '&[data-shrink="true"]': {
        color: theme.palette.text.disabled,
      },
      '&.Mui-focused': {
        color: theme.palette.common.white,
      },
    },
  },
  accountActionText: {
    color: theme.palette.info.main,
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: 600,
  },
  paymentMethodsPanel: {
    border: 'none',
    borderRadius: 0,
    padding: 0,
    background: 'transparent',
    boxShadow: 'none',
  },
  paymentMethodsHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '16px',
  },
  paymentMethodsStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  paymentAccordion: {
    marginBottom: 0,
    border: '1px solid rgb(0, 102, 124)',
    borderRadius: '10px',
    backgroundColor: 'rgba(255, 255, 255, 0.02)',
    overflow: 'hidden',
    transition: 'border-color 220ms ease',
    '&.Mui-expanded': {
      borderColor: 'rgb(0, 102, 124)',
      margin: 0,
    },
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  paymentAccordionAch: {
    '& .MuiAccordionSummary-root': {
      borderLeft: '4px solid rgb(0, 102, 124)',
    },
  },
  paymentAccordionCheck: {
    '& .MuiAccordionSummary-root': {
      borderLeft: '4px solid rgb(0, 102, 124)',
    },
  },
  paymentAccordionUpcoming: {
    borderColor: 'rgb(0, 102, 124)',
    '& .MuiAccordionSummary-root': {
      borderLeft: '4px solid rgb(0, 102, 124)',
    },
  },
  paymentSummaryTitleWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  paymentSummaryTitle: {
    color: theme.palette.common.white,
    fontWeight: 600,
    fontSize: '14px',
    fontFamily: 'Inter',
  },
  paymentSummaryCount: {
    color: theme.palette.text.secondary,
    fontWeight: 500,
    fontSize: '12px',
    fontFamily: 'Inter',
    lineHeight: '16px',
  },
  paymentDefaultWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  paymentDefaultLabel: {
    color: theme.palette.common.white,
    fontSize: '12px',
    fontFamily: 'Inter',
  },
  paymentDefaultRadio: {
    color: '#5e6174',
    '&.Mui-checked': {
      color: '#2DD4BF',
    },
    padding: '4px',
  },
  paymentAccordionDetails: {
    padding: '16px 24px 24px',
  },
  paymentFieldsStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    '& .MuiFormHelperText-root': {
      position: 'relative',
      bottom: 'auto',
      marginTop: '6px',
      marginLeft: 0,
      minHeight: '18px',
      lineHeight: '18px',
    },
  },
  paymentSectionLabel: {
    color: theme.palette.text.disabled,
    fontFamily: 'Inter',
    fontSize: '16px',
    fontWeight: 500,
  },
  rightColumnFieldTypography: {
    '& .MuiInputLabel-root': {
      fontFamily: 'Inter',
      fontSize: '16px',
      fontWeight: 400,
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: `${theme.palette.common.white} !important`,
    },
    '& .MuiOutlinedInput-input': {
      fontFamily: 'Inter',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '120%',
    },
    '& .MuiSelect-select': {
      fontFamily: 'Inter',
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '120%',
    },
    '& .MuiFormHelperText-root': {
      position: 'relative',
      bottom: 'auto',
      marginTop: '6px',
      marginLeft: 0,
      minHeight: '18px',
      lineHeight: '18px',
    },
  },
  infoIconButton: {
    color: theme.palette.text.secondary,
    padding: '2px',
    '&:hover': {
      color: theme.palette.common.white,
    },
  },
}));

const VendorCreateFormContainer = () => {
  const ROUTING_LOOKUP_UNAVAILABLE_MESSAGE =
    'Unable to verify bank name right now. You can still continue with a valid routing number.';
  // Synchronize mailing address verification flags with participation address verification when 'Same as Vendor Address' is checked
  const styles = useStyles();
  const navigate = useNavigate();
  const { setAlert } = useSnackBar();

const vendorEntity = accountingVendorType;;

  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);
  const [pendingSubmissionData, setPendingSubmissionData] =
    useState<VendorAccountSubmitFormProps | null>(null);
  const [searchVendorOpen, setSearchVendorOpen] = useState(false);
  const [vendorConfirmationOpen, setVendorConfirmationOpen] = useState(false);
  const [isVendorVisibilityRestricted, setIsVendorVisibilityRestricted] = useState<boolean>(false);
  const [isLoadingVendorConfig, setIsLoadingVendorConfig] = useState<boolean>(true);
  const [findVendorConfig, setFindVendorConfig] = useState<string | null>(null);
  // const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  // Accordion expansion states (independent of default method selection)
  const [achAccordionExpanded, setAchAccordionExpanded] = useState(true);
  const [checkAccordionExpanded, setCheckAccordionExpanded] = useState(true);

  // Track whether addresses were selected from autocomplete vs manually typed
  const [participationAddressFromAutocomplete, setParticipationAddressFromAutocomplete] =
    useState(false);
  const [mailingAddressFromAutocomplete, setMailingAddressFromAutocomplete] = useState(false);

  // Track addresses verified through popup (to prevent blur from resetting verification)
  const [participationAddressVerifiedByPopup, setParticipationAddressVerifiedByPopup] =
    useState(false);
  const [mailingAddressVerifiedByPopup, setMailingAddressVerifiedByPopup] = useState(false);

  // Track addresses marked as 'keep as entered' (custom addresses)
  const [participationAddressKeptAsEntered, setParticipationAddressKeptAsEntered] = useState(false);
  const [mailingAddressKeptAsEntered, setMailingAddressKeptAsEntered] = useState(false);

  // Track the original addresses that were 'kept as entered' for comparison
  const [originalParticipationKeptAddress, setOriginalParticipationKeptAddress] = useState('');
  const [originalMailingKeptAddress, setOriginalMailingKeptAddress] = useState('');

  // Key to force re-render of mailing address fields when checkbox is toggled
  // Key to force re-render of participation address fields when checkbox is toggled
  const [mailingAddressKey, setMailingAddressKey] = useState(0);
  const [participationAddressKey, setParticipationAddressKey] = useState(0);
  const [achNickname, setAchNickname] = useState('');
  const [checkNickname, setCheckNickname] = useState('');
  const [checkPayeeName, setCheckPayeeName] = useState('');
  const [checkRecipientName, setCheckRecipientName] = useState('');
  const [isCheckPayeeNameManuallyEdited, setIsCheckPayeeNameManuallyEdited] = useState(false);
  const [isCheckRecipientNameManuallyEdited, setIsCheckRecipientNameManuallyEdited] =
    useState(false);
  const [isSyncingFromSameAsVendor, setIsSyncingFromSameAsVendor] = useState(false);
  const [routingBankName, setRoutingBankName] = useState<string | null>(null);
  const [routingLookupError, setRoutingLookupError] = useState<string | null>(null);
  const latestRoutingRef = useRef('');
  const routingLookupCacheRef = useRef<Record<string, string | null>>({});
  const routingLookupFailedCacheRef = useRef<Record<string, boolean>>({});

  const vendorCreateResolver = yupResolver(CreateVendorAccountFormSchema);

  const formMethods = useForm<Parameters<typeof vendorCreateResolver>[0]>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: vendorCreateResolver,
    defaultValues: VENDOR_ACCOUNT_CREATE_DEFAULT_VALUES,
  });

  const {
    formState: {
      isValid,
      isDirty,
      isSubmitting,
      isValidating,
      errors,
      touchedFields,
      dirtyFields,
      submitCount,
    },
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    trigger,
    getValues,
  } = formMethods;

  const achEnabled = useWatch({
    control: formMethods.control,
    name: 'achEnabled',
    defaultValue: true,
  });
  const checkMailingEnabled = useWatch({
    control: formMethods.control,
    name: 'checkMailingEnabled',
    defaultValue: false,
  });
  const defaultAchEnabled = useWatch({
    control: formMethods.control,
    name: 'defaultAchEnabled',
    defaultValue: true,
  });
  const defaultCheckMailingEnabled = useWatch({
    control: formMethods.control,
    name: 'defaultCheckMailingEnabled',
    defaultValue: false,
  });
  const sameAsVendorAddress = useWatch({
    control: formMethods.control,
    name: 'sameAsVendorAddress',
    defaultValue: false,
  });

  const participationAddress1 = useWatch({
    control: formMethods.control,
    name: 'participationAddress1',
  });
  const vendorName = useWatch({ control: formMethods.control, name: 'name' });
  const participationAddress2 = useWatch({
    control: formMethods.control,
    name: 'participationAddress2',
  });
  const participationCity = useWatch({ control: formMethods.control, name: 'participationCity' });
  const participationState = useWatch({ control: formMethods.control, name: 'participationState' });
  const participationZip = useWatch({ control: formMethods.control, name: 'participationZip' });
  const hasParticipationAddress = [
    participationAddress1,
    participationAddress2,
    participationCity,
    participationState,
    participationZip,
  ].some((value) => (value || '').trim() !== '');
  // Only a complete vendor address can be verified
  const isParticipationAddressComplete = [
    participationAddress1,
    participationCity,
    participationState,
    participationZip,
  ].every((value) => (value || '').trim() !== '');

  const mailingAddress1 = useWatch({ control: formMethods.control, name: 'mailingAddress1' });
  const mailingAddress2 = useWatch({ control: formMethods.control, name: 'mailingAddress2' });
  const mailingCity = useWatch({ control: formMethods.control, name: 'mailingCity' });
  const mailingState = useWatch({ control: formMethods.control, name: 'mailingState' });
  const mailingZip = useWatch({ control: formMethods.control, name: 'mailingZip' });

  // Watch ACH fields to determine if user has entered any data
  const achType = useWatch({ control: formMethods.control, name: 'accountType' });
  const achAccountNumber = useWatch({ control: formMethods.control, name: 'accountNumber' });
  const achRoutingNumber = useWatch({ control: formMethods.control, name: 'routingNumber' });

  // Watch Check fields to determine if user has entered any data
  const mailingPhoneNumber = useWatch({ control: formMethods.control, name: 'mailingPhoneNumber' });
  const hasAchDefaultCandidate =
    !!(achType || '').toString().trim() &&
    !!(achRoutingNumber || '').toString().trim() &&
    !!(achAccountNumber || '').toString().trim();
  const hasCheckDefaultCandidate =
    !!(mailingAddress1 || '').trim() &&
    !!(mailingCity || '').trim() &&
    !!(mailingState || '').trim() &&
    !!(mailingZip || '').trim();
  const achConfiguredCount = hasAchDefaultCandidate ? 1 : 0;
  const checkConfiguredCount = hasCheckDefaultCandidate ? 1 : 0;

  const validateRoutingChecksum = (routing: string): boolean => {
    if (routing.length !== 9) return false;

    const d = routing.split('').map(Number);
    const checksum =
      (3 * (d[0] + d[3] + d[6]) + 7 * (d[1] + d[4] + d[7]) + 1 * (d[2] + d[5] + d[8])) % 10;

    return checksum === 0;
  };

  const isValidRoutingNumber = (routing: string): boolean => {
    if (!/^\d{9}$/.test(routing)) return false;
    return validateRoutingChecksum(routing);
  };

  const validateRoutingNumberLookup = async (lookupKey: string): Promise<void> => {
    setRoutingLookupError(null);

    if (!isValidRoutingNumber(lookupKey)) {
      setRoutingBankName(null);
      return;
    }

    const cached = routingLookupCacheRef.current[lookupKey];
    if (cached !== undefined) {
      setRoutingBankName(cached);
      setRoutingLookupError(
        routingLookupFailedCacheRef.current[lookupKey]
          ? ROUTING_LOOKUP_UNAVAILABLE_MESSAGE
          : null,
      );
      return;
    }

    try {
      const bank = await getBankRoutingDetails(lookupKey);
      const resolvedBankName = bank.bankName?.trim() || null;
      routingLookupCacheRef.current[lookupKey] = resolvedBankName;
      routingLookupFailedCacheRef.current[lookupKey] = !resolvedBankName;

      if (latestRoutingRef.current === lookupKey) {
        setRoutingBankName(resolvedBankName);
        setRoutingLookupError(resolvedBankName ? null : ROUTING_LOOKUP_UNAVAILABLE_MESSAGE);
      }
    } catch {
      routingLookupCacheRef.current[lookupKey] = null;
      routingLookupFailedCacheRef.current[lookupKey] = true;
      if (latestRoutingRef.current === lookupKey) {
        setRoutingBankName(null);
        setRoutingLookupError(ROUTING_LOOKUP_UNAVAILABLE_MESSAGE);
      }
    }
  };

  const handleRoutingNumberChange = (rawValue: string) => {
    const nextValue = rawValue.replace(/\D/g, '').slice(0, 9);
    setValue('routingNumber', nextValue, { shouldDirty: true, shouldValidate: true });
    latestRoutingRef.current = nextValue;

    if (nextValue !== (achRoutingNumber || '')) {
      setRoutingBankName(null);
      setRoutingLookupError(null);
    }
  };

  const handleRoutingNumberBlur = async (rawValue?: string) => {
    const lookupKey = (rawValue ?? achRoutingNumber ?? '').replace(/\D/g, '').slice(0, 9);
    await validateRoutingNumberLookup(lookupKey);
  };

  useEffect(() => {
    const lookupKey = (achRoutingNumber || '').replace(/\D/g, '').slice(0, 9);
    latestRoutingRef.current = lookupKey;

    if (isValidRoutingNumber(lookupKey)) {
      void validateRoutingNumberLookup(lookupKey);
    }
  }, [achRoutingNumber]);

  useEffect(() => {
    if (!isCheckPayeeNameManuallyEdited) {
      setCheckPayeeName(vendorName || '');
    }
  }, [vendorName, isCheckPayeeNameManuallyEdited]);

  useEffect(() => {
    if (!isCheckRecipientNameManuallyEdited) {
      setCheckRecipientName(vendorName || '');
    }
  }, [vendorName, isCheckRecipientNameManuallyEdited]);

  // const [isSelectingNetSuiteVendor, setIsSelectingNetSuiteVendor] = useState(false);
  const [isNetSuitePopupOpen, setIsNetSuitePopupOpen] = useState(false);

  const getProviderStringValue = (providerData: Record<string, unknown> | undefined, key: string): string => {
    if (!providerData) return '';
    const value = providerData[key];
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    return '';
  };

  const getVendorTin = (vendorData: NetSuiteVendor): string => {
    return (
      getProviderStringValue(vendorData.providerData, 'taxId') ||
      getProviderStringValue(vendorData.providerData, 'taxIdLast4')
    );
  };
  


  useEffect(() => {
    getVendorConfig()
      .then((res) => {
        setIsVendorVisibilityRestricted(res.data.isVendorVisibilityRestricted)
        setFindVendorConfig(res.data.findVendorSource)
      })
      .catch(() => {
        setAlert({
          message: 'Failed to load vendor configuration.',
          type: 'error',
        });
        // Silently fall back to showing the Find Vendor button
      })
      .finally(() => setIsLoadingVendorConfig(false));
  }, []);

  // Convert a state from an external source to the dropdown's code ('WI').
  // Accepts a code or a full name in any case; anything else becomes ''.
  const toStateCode = (state?: string) => {
    const value = (state || '').trim().toLowerCase();
    if (!value) return '';
    const match = US_STATES_LIST.find(
      (option) => option.id.toLowerCase() === value || option.label.toLowerCase() === value,
    );
    return match ? match.id : '';
  };

  // Fill the vendor address from a trusted source (NetSuite or VLaw vendor search).
  // The address counts as verified only when Address 1, City, State and Zip are all present;
  // a partial or missing address is filled in as-is and left unverified.
  const applyPrefilledParticipationAddress = (
    address: { address1?: string; address2?: string; city?: string; state?: string; zip?: string } | null | undefined,
    verifiedAs: 'autocomplete' | 'popup',
  ) => {
    const prefilled = {
      address1: address?.address1 || '',
      address2: address?.address2 || '',
      city: address?.city || '',
      state: toStateCode(address?.state),
      zip: address?.zip || '',
    };

    // Always overwrite, so a vendor without an address doesn't keep the previous vendor's address
    setValue('participationAddress1', prefilled.address1);
    setValue('participationAddress2', prefilled.address2);
    setValue('participationCity', prefilled.city);
    setValue('participationState', prefilled.state);
    setValue('participationZip', prefilled.zip);

    const isComplete = [prefilled.address1, prefilled.city, prefilled.state, prefilled.zip].every(
      (value) => value.trim() !== '',
    );

    // Only a complete address is remembered as "last verified"; otherwise the restore effect
    // would later mark a partial address as verified because the fields still match it.
    setLastAutoCompleteAddressState(
      isComplete ? prefilled : { address1: '', address2: '', city: '', state: '', zip: '' },
    );
    setOriginalParticipationKeptAddress('');
    setParticipationAddressFromAutocomplete(isComplete && verifiedAs === 'autocomplete');
    setParticipationAddressVerifiedByPopup(isComplete && verifiedAs === 'popup');
    setParticipationAddressKeptAsEntered(false);
    formMethods.setValue('isAddressVerified', isComplete, { shouldValidate: true });

    if (!address) {
      clearErrors([
        'participationAddress1',
        'participationAddress2',
        'participationCity',
        'participationState',
        'participationZip',
      ]);
    }
  };

  const handleVendorSelectFromNetSuite = (vendorData: NetSuiteVendor) => {
    const vendorTin = getVendorTin(vendorData);

    setValue('name', vendorData.name || '');
    setValue('email', vendorData.email || '');
    setValue('tin', vendorTin);
    // setValue('contactName', vendorData.name || '');
    setValue('externalVendorId', vendorData.internalId || null);
    setValue('vendorType', 'LIEN_HOLDER');

    const hasAddress = Boolean(vendorData.address1 || vendorData.address2 || vendorData.city || vendorData.state || vendorData.zip);
    applyPrefilledParticipationAddress(
      hasAddress
        ? {
            address1: vendorData.address1 || '',
            address2: vendorData.address2 || '',
            city: vendorData.city || '',
            state: vendorData.state || '',
            zip: vendorData.zip || '',
          }
        : undefined,
      'autocomplete',
    );
    setTimeout(() => {
      const fieldsToTrigger: FieldPath<FormValues>[] = [];

      if (vendorData.name) fieldsToTrigger.push('name');
      if (vendorData.email) fieldsToTrigger.push('email');
      if (vendorTin) fieldsToTrigger.push('tin');
      fieldsToTrigger.push('vendorType');
      // if (vendorData.name) fieldsToTrigger.push('contactName');

      if (vendorData.address1) fieldsToTrigger.push('participationAddress1');
      if (vendorData.address2) fieldsToTrigger.push('participationAddress2');
      if (vendorData.city) fieldsToTrigger.push('participationCity');
      if (vendorData.state) fieldsToTrigger.push('participationState');
      if (vendorData.zip) fieldsToTrigger.push('participationZip');

      if (fieldsToTrigger.length > 0) {
        trigger(fieldsToTrigger);
      }
    }, 0);
    setIsNetSuitePopupOpen(false);
  }

  useEffect(() => {
    const storedVendorData = sessionStorage.getItem('netsuiteVendorData');

    if (!storedVendorData) {
      reset(VENDOR_ACCOUNT_CREATE_DEFAULT_VALUES);
      return;
    }

    // Vendor opened from another page ("Continue to V.Law vendor creation" in a new tab)
    handleVendorSelectFromNetSuite(JSON.parse(storedVendorData) as NetSuiteVendor);
    sessionStorage.removeItem('netsuiteVendorData');
  }, []);

  const handleSearchVendorClick = () => {
    setSearchVendorOpen(true);
  };

  // Track which address verification was triggered
  const [addressVerificationType, setAddressVerificationType] = useState<
    'participation' | 'mailing'
  >('participation');
  const [isManualVerification, setIsManualVerification] = useState(false);

  const handleVerifyAddress = async () => {
    if (!participationAddress1) {
      setAlert((prev) => ({
        ...prev,
        message: 'Please enter an address to verify.',
        type: 'warning',
      }));
      return;
    }
    setAddressVerificationType('participation');
    setIsManualVerification(true);
    setVendorConfirmationOpen(true);
  };

  const handleVerifyMailingAddress = async () => {
    if (!mailingAddress1 || !mailingCity || !mailingState || !mailingZip) {
      setAlert((prev) => ({
        ...prev,
        message: 'Please complete mailing address fields before verification.',
        type: 'warning',
      }));
      return;
    }

    setAddressVerificationType('mailing');
    setIsManualVerification(true);
    setVendorConfirmationOpen(true);
  };

  // Type for vendor search result
  interface VendorSearchResult {
    name: string;
    tin: string | null;
    address1?: string;
    address2?: string;
    city?: string;
    countryState?: string;
    zip?: string;
  }

  const handleVendorSelect = (vendor: VendorSearchResult) => {
    setValue('name', vendor.name);
    setValue('tin', vendor.tin ?? '');
    // Populate participation address fields from selected vendor (verified only if complete)
    applyPrefilledParticipationAddress(
      {
        address1: vendor.address1,
        address2: vendor.address2,
        city: vendor.city,
        state: vendor.countryState,
        zip: vendor.zip,
      },
      'popup',
    );
  };

  const handleParticipationAddressSelect = async (address: AddressPrediction | null) => {
    if (!address) {
      setValue('participationAddress1', '', { shouldDirty: true, shouldValidate: true });
      setParticipationAddressFromAutocomplete(false);
      setParticipationAddressVerifiedByPopup(false);
      setParticipationAddressKeptAsEntered(false);
      return;
    }

    const addressText = address?.description || '';

    // Set the updating flag FIRST, before any setValue calls that might trigger useEffects
    if (address?.placeId) {
      setIsUpdatingFromAutocomplete(true);
    }

    if (addressText) {
      setValue('participationAddress1', addressText);
      if (address?.placeId) {
        setParticipationAddressFromAutocomplete(true);
      } else {
        setParticipationAddressFromAutocomplete(false);
      }

      if (address?.placeId) {
        try {
          const validatedAddress = await validateAddress(addressText, address?.placeId);

          if (validatedAddress.data) {
            const { addressLine1, addressLine2, city, state, zip } = validatedAddress.data;
            if (addressLine1) {
              setValue('participationAddress1', addressLine1);
            } else {
              setValue('participationAddress1', '');
            }
            if (addressLine2) {
              setValue('participationAddress2', addressLine2);
            } else {
              setValue('participationAddress2', '');
            }
            if (city) setValue('participationCity', city); else { setValue('participationCity', '') };
            if (state) setValue('participationState', state); else { setValue('participationState', '') };
            if (zip) setValue('participationZip', zip); else { setValue('participationZip', '') };

            setLastAutoCompleteAddressState({
              address1: addressLine1 || '',
              address2: addressLine2 || '',
              city: city || '',
              state: state || '',
              zip: zip || '',
            });

            clearErrors([
              'participationAddress1',
              'participationAddress2',
              'participationCity',
              'participationState',
              'participationZip',
            ]);

            trigger([
              'participationAddress1',
              'participationAddress2',
              'participationCity',
              'participationState',
              'participationZip',
            ]);
          }

          // Re-enable change tracking after a brief delay
          setTimeout(() => {
            setIsUpdatingFromAutocomplete(false);
          }, 500);
        } catch (error) {
          setValue('participationAddress1', addressText);
          setIsUpdatingFromAutocomplete(false);
        }
      }
    }
  };

  const handleMailingAddressSelect = async (address: AddressPrediction) => {
    if (!address) return;
    const addressText = address?.description || '';

    if (addressText) {
      setValue('mailingAddress1', addressText);
      if (address?.placeId) {
        setMailingAddressFromAutocomplete(true);
      } else {
        setMailingAddressFromAutocomplete(false);
      }

      if (address?.placeId) {
        try {
          setIsUpdatingMailingFromAutocomplete(true);
          const validatedAddress = await validateAddress(addressText, address?.placeId);

          if (validatedAddress.data) {
            const { addressLine1, addressLine2, city, state, zip } = validatedAddress.data;
            if (addressLine1) {
              setValue('mailingAddress1', addressLine1);
              // setLastMailingAutoCompleteAddress(addressText);
            } else {
              setValue('mailingAddress1', '');
            }
            if (addressLine2) {
              setValue('mailingAddress2', addressLine2);
            } else {
              setValue('mailingAddress2', '');
            }
            if (city) setValue('mailingCity', city); else { setValue('mailingCity', '') };
            if (state) setValue('mailingState', state); else { setValue('mailingState', '') };
            if (zip) setValue('mailingZip', zip); else { setValue('mailingZip', '') };

            setLastMailingAutoCompleteAddressState({
              address1: addressLine1 || '',
              address2: addressLine2 || '',
              city: city || '',
              state: state || '',
              zip: zip || '',
            });

            clearErrors([
              'mailingAddress1',
              'mailingAddress2',
              'mailingCity',
              'mailingState',
              'mailingZip',
            ]);

            trigger([
              'mailingAddress1',
              'mailingAddress2',
              'mailingCity',
              'mailingState',
              'mailingZip',
            ]);
          }
          // Re-enable change tracking after a brief delay
          setTimeout(() => setIsUpdatingMailingFromAutocomplete(false), 100);
        } catch (error) {
          setValue('mailingAddress1', addressText);
          // setLastMailingAutoCompleteAddress(addressText);
          setIsUpdatingMailingFromAutocomplete(false);
        }
      }
    }
  };

  useEffect(() => {
    if (!achEnabled) {
      clearErrors(['accountType', 'accountNumber', 'routingNumber']);
    }
  }, [achEnabled, clearErrors]);

  useEffect(() => {
    if (!checkMailingEnabled) {
      clearErrors([
        'mailingAddress1',
        'mailingAddress2',
        'mailingCity',
        'mailingState',
        'mailingZip',
        'mailingPhoneNumber',
      ]);
    }
  }, [checkMailingEnabled, clearErrors]);

  const isMailingSameAsParticipationAddress = () =>
    (mailingAddress1 || '').trim() === (participationAddress1 || '').trim() &&
    (mailingAddress2 || '').trim() === (participationAddress2 || '').trim() &&
    (mailingCity || '').trim() === (participationCity || '').trim() &&
    (mailingState || '').trim().toUpperCase() === (participationState || '').trim().toUpperCase() &&
    (mailingZip || '').trim() === (participationZip || '').trim();

  // Handle 'Same as Vendor Address' checkbox
  useEffect(() => {
    if (sameAsVendorAddress) {
      setIsSyncingFromSameAsVendor(true);
      setValue('mailingAddress1', participationAddress1 || '');
      setValue('mailingAddress2', participationAddress2 || '');
      setValue('mailingCity', participationCity || '');
      setValue('mailingState', participationState || '');
      setValue('mailingZip', participationZip || '');

      if (participationAddressFromAutocomplete) {
        setMailingAddressFromAutocomplete(true);
        setLastMailingAutoCompleteAddressState({
          address1: participationAddress1 || '',
          address2: participationAddress2 || '',
          city: participationCity || '',
          state: participationState || '',
          zip: participationZip || '',
        });
        formMethods.setValue('isMailingAddressVerified', true, { shouldValidate: true });
      } else if (participationAddressVerifiedByPopup) {
        setMailingAddressVerifiedByPopup(true);
        formMethods.setValue('isMailingAddressVerified', true, { shouldValidate: true });
      } else if (participationAddressKeptAsEntered) {
        setMailingAddressKeptAsEntered(true);
        setOriginalMailingKeptAddress(
          `${participationAddress1}, ${participationCity}, ${participationState} ${participationZip}`,
        );
        formMethods.setValue('isMailingAddressVerified', false, { shouldValidate: true });
      }
      trigger(['mailingAddress1', 'mailingAddress2', 'mailingCity', 'mailingState', 'mailingZip']);
    } else {
      setIsSyncingFromSameAsVendor(false);
    }
  }, [
    sameAsVendorAddress,
    participationAddress1,
    participationAddress2,
    participationCity,
    participationState,
    participationZip,
    setValue,
    trigger,
    formMethods,
  ]);

  // End programmatic sync only after copied mailing fields match vendor fields.
  useEffect(() => {
    if (!sameAsVendorAddress || !isSyncingFromSameAsVendor) {
      return;
    }

    if (isMailingSameAsParticipationAddress()) {
      setIsSyncingFromSameAsVendor(false);
    }
  }, [
    sameAsVendorAddress,
    isSyncingFromSameAsVendor,
    mailingAddress1,
    mailingAddress2,
    mailingCity,
    mailingState,
    mailingZip,
    participationAddress1,
    participationAddress2,
    participationCity,
    participationState,
    participationZip,
  ]);

  useEffect(() => {
    if (!sameAsVendorAddress || isSyncingFromSameAsVendor) return;

    const isStillSameAsVendor = isMailingSameAsParticipationAddress();

    if (!isStillSameAsVendor) {
      formMethods.setValue('sameAsVendorAddress', false, { shouldDirty: true });
      setMailingAddressFromAutocomplete(false);
      setMailingAddressVerifiedByPopup(false);
      setMailingAddressKeptAsEntered(false);
      setOriginalMailingKeptAddress('');
    }
  }, [
    sameAsVendorAddress,
    isSyncingFromSameAsVendor,
    mailingAddress1,
    mailingAddress2,
    mailingCity,
    mailingState,
    mailingZip,
    participationAddress1,
    participationAddress2,
    participationCity,
    participationState,
    participationZip,
    formMethods,
  ]);

  useEffect(() => {
    if (sameAsVendorAddress) {
      // If participation address is verified by autocomplete
      if (participationAddressFromAutocomplete) {
        setMailingAddressFromAutocomplete(true);
        setMailingAddressVerifiedByPopup(false);
        setMailingAddressKeptAsEntered(false);
        formMethods.setValue('isMailingAddressVerified', true, { shouldValidate: true });
      } else if (participationAddressVerifiedByPopup) {
        setMailingAddressVerifiedByPopup(true);
        setMailingAddressFromAutocomplete(false);
        setMailingAddressKeptAsEntered(false);
        formMethods.setValue('isMailingAddressVerified', true, { shouldValidate: true });
      } else if (participationAddressKeptAsEntered) {
        setMailingAddressKeptAsEntered(true);
        setMailingAddressFromAutocomplete(false);
        setMailingAddressVerifiedByPopup(false);
        setOriginalMailingKeptAddress(
          `${participationAddress1}, ${participationCity}, ${participationState} ${participationZip}`,
        );
        formMethods.setValue('isMailingAddressVerified', false, { shouldValidate: true });
      } else {
        // If none are verified, clear all mailing verification flags
        setMailingAddressFromAutocomplete(false);
        setMailingAddressVerifiedByPopup(false);
        setMailingAddressKeptAsEntered(false);
        setOriginalMailingKeptAddress('');
        formMethods.setValue('isMailingAddressVerified', false, { shouldValidate: true });
      }
    }
  }, [
    sameAsVendorAddress,
    participationAddressFromAutocomplete,
    participationAddressVerifiedByPopup,
    participationAddressKeptAsEntered,
    participationAddress1,
    participationCity,
    participationState,
    participationZip,
    formMethods,
  ]);

  // Track complete autocomplete address state
  const [lastAutoCompleteAddressState, setLastAutoCompleteAddressState] = useState({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
  });
  const [lastMailingAutoCompleteAddressState, setLastMailingAutoCompleteAddressState] = useState({
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
  });

  // Flags to temporarily disable change tracking during autocomplete updates
  const [isUpdatingFromAutocomplete, setIsUpdatingFromAutocomplete] = useState(false);
  const [isUpdatingMailingFromAutocomplete, setIsUpdatingMailingFromAutocomplete] = useState(false);

  useEffect(() => {
    // Don't reset flags if we just set participationAddressFromAutocomplete to true
    if (participationAddressFromAutocomplete && !lastAutoCompleteAddressState.address1) {
      return;
    }

    // Helper to compare address fields (excluding Address2)
    const isParticipationAddressSameAsVerified =
      participationAddress1 === lastAutoCompleteAddressState.address1 &&
      participationCity === lastAutoCompleteAddressState.city &&
      participationState === lastAutoCompleteAddressState.state &&
      participationZip === lastAutoCompleteAddressState.zip;

    // If the address changes and it's not from autocomplete, reset all verification flags
    // But if the address is the same as the last verified, restore the flags
    if (!isUpdatingFromAutocomplete && lastAutoCompleteAddressState.address1) {
      if (
        isParticipationAddressSameAsVerified &&
        !(participationAddressFromAutocomplete || participationAddressVerifiedByPopup)
      ) {
        // Restore verification flags if address matches last verified
        setParticipationAddressFromAutocomplete(true);
        setParticipationAddressVerifiedByPopup(false);
        setParticipationAddressKeptAsEntered(false);
        formMethods.setValue('isAddressVerified', true, { shouldValidate: true });
      } else if (
        !isParticipationAddressSameAsVerified &&
        (participationAddressFromAutocomplete || participationAddressVerifiedByPopup)
      ) {
        // Reset verification flags if address no longer matches
        setParticipationAddressFromAutocomplete(false);
        setParticipationAddressVerifiedByPopup(false);
        setParticipationAddressKeptAsEntered(false);
        formMethods.setValue('isAddressVerified', false, { shouldValidate: true });
      }
    }

    if (
      !isUpdatingFromAutocomplete &&
      participationAddressKeptAsEntered &&
      originalParticipationKeptAddress
    ) {
      const currentFullAddress = `${participationAddress1}, ${participationCity}, ${participationState} ${participationZip}`;
      if (currentFullAddress !== originalParticipationKeptAddress) {
        setParticipationAddressKeptAsEntered(false);
        setOriginalParticipationKeptAddress('');
      }
    }
  }, [
    participationAddress1,
    participationCity,
    participationState,
    participationZip,
    lastAutoCompleteAddressState,
    participationAddressFromAutocomplete,
    participationAddressVerifiedByPopup,
    participationAddressKeptAsEntered,
    isUpdatingFromAutocomplete,
    originalParticipationKeptAddress,
  ]);

  useEffect(() => {
    // Skip this tracking when 'Same as Vendor Address' is checked,
    if (sameAsVendorAddress) return;

    // Helper to compare address fields (excluding Address2)
    const isMailingAddressSameAsVerified =
      mailingAddress1 === lastMailingAutoCompleteAddressState.address1 &&
      mailingCity === lastMailingAutoCompleteAddressState.city &&
      mailingState === lastMailingAutoCompleteAddressState.state &&
      mailingZip === lastMailingAutoCompleteAddressState.zip;

    if (!isUpdatingMailingFromAutocomplete && lastMailingAutoCompleteAddressState.address1) {
      if (
        isMailingAddressSameAsVerified &&
        !(mailingAddressFromAutocomplete || mailingAddressVerifiedByPopup)
      ) {
        // Restore verification flags if address matches last verified
        setMailingAddressFromAutocomplete(true);
        setMailingAddressVerifiedByPopup(false);
        setMailingAddressKeptAsEntered(false);
        formMethods.setValue('isMailingAddressVerified', true, { shouldValidate: true });
      } else if (
        !isMailingAddressSameAsVerified &&
        (mailingAddressFromAutocomplete || mailingAddressVerifiedByPopup)
      ) {
        // Reset verification flags if address no longer matches
        setMailingAddressFromAutocomplete(false);
        setMailingAddressVerifiedByPopup(false);
        setMailingAddressKeptAsEntered(false);
        formMethods.setValue('isMailingAddressVerified', false, { shouldValidate: true });
      }
    }

    // Also reset 'keep as entered' if user manually changes address after keeping it as entered
    if (
      !isUpdatingMailingFromAutocomplete &&
      mailingAddressKeptAsEntered &&
      originalMailingKeptAddress
    ) {
      const currentFullAddress = `${mailingAddress1}, ${mailingCity}, ${mailingState} ${mailingZip}`;
      if (currentFullAddress !== originalMailingKeptAddress) {
        setMailingAddressKeptAsEntered(false);
        setOriginalMailingKeptAddress('');
      }
    }
  }, [
    mailingAddress1,
    mailingCity,
    mailingState,
    mailingZip,
    lastMailingAutoCompleteAddressState,
    mailingAddressFromAutocomplete,
    mailingAddressVerifiedByPopup,
    mailingAddressKeptAsEntered,
    isUpdatingMailingFromAutocomplete,
    sameAsVendorAddress,
    originalMailingKeptAddress,
  ]);

  // Track changes to any participation address fields after autocomplete selection
  useEffect(() => {
    if (isUpdatingFromAutocomplete) return;
    if (
      (participationAddressFromAutocomplete || participationAddressVerifiedByPopup) &&
      lastAutoCompleteAddressState.address1 &&
      (participationAddress1 !== lastAutoCompleteAddressState.address1 ||
        participationCity !== lastAutoCompleteAddressState.city ||
        participationState !== lastAutoCompleteAddressState.state ||
        participationZip !== lastAutoCompleteAddressState.zip)
    ) {
      // Any field has been manually changed after autocomplete selection
      setParticipationAddressFromAutocomplete(false);
      setParticipationAddressVerifiedByPopup(false);
      setParticipationAddressKeptAsEntered(false);
      setOriginalParticipationKeptAddress('');
      // Do NOT clear lastAutoCompleteAddressState, so reverting restores verification
    }
  }, [
    participationAddress1,
    participationCity,
    participationState,
    participationZip,
    participationAddressFromAutocomplete,
    participationAddressVerifiedByPopup,
    lastAutoCompleteAddressState,
    isUpdatingFromAutocomplete,
  ]);

  // Track changes to any mailing address fields after autocomplete selection
  useEffect(() => {
    // Skip tracking if we're currently updating from autocomplete
    if (isUpdatingMailingFromAutocomplete) return;

    // Skip this tracking when 'Same as Vendor Address' is checked
    if (sameAsVendorAddress) return;

    if (
      (mailingAddressFromAutocomplete || mailingAddressVerifiedByPopup) &&
      lastMailingAutoCompleteAddressState.address1 &&
      (mailingAddress1 !== lastMailingAutoCompleteAddressState.address1 ||
        mailingCity !== lastMailingAutoCompleteAddressState.city ||
        mailingState !== lastMailingAutoCompleteAddressState.state ||
        mailingZip !== lastMailingAutoCompleteAddressState.zip)
    ) {
      // Any field has been manually changed after autocomplete selection
      setMailingAddressFromAutocomplete(false);
      setMailingAddressVerifiedByPopup(false);
      setMailingAddressKeptAsEntered(false);
      setOriginalMailingKeptAddress('');
      // Do NOT clear lastMailingAutoCompleteAddressState, so reverting restores verification
    }
  }, [
    mailingAddress1,
    mailingCity,
    mailingState,
    mailingZip,
    mailingAddressFromAutocomplete,
    mailingAddressVerifiedByPopup,
    lastMailingAutoCompleteAddressState,
    isUpdatingMailingFromAutocomplete,
    sameAsVendorAddress,
  ]);

  const checkForCustomAddresses = () => {
    const hasCustomParticipationAddress =
      isParticipationAddressComplete &&
      !participationAddressFromAutocomplete &&
      !participationAddressVerifiedByPopup &&
      !participationAddressKeptAsEntered;
    const hasCustomMailingAddress =
      mailingAddress1 &&
      !mailingAddressFromAutocomplete &&
      !mailingAddressVerifiedByPopup &&
      !mailingAddressKeptAsEntered &&
      checkMailingEnabled;

    if (hasCustomParticipationAddress || hasCustomMailingAddress) {
      // Show vendor confirmation popup instead of address confirmation
      setIsManualVerification(false); // Mark as form submission verification
      setVendorConfirmationOpen(true);
      return false;
    }
    return true;
  };

  const handleVendorConfirmation = (selectedVendor?: VendorOption, keepAsEntered?: boolean) => {
    setVendorConfirmationOpen(false);

    // Handle 'keep as entered' flag from popup
    if (keepAsEntered) {
      if (addressVerificationType === 'participation') {
        setParticipationAddressKeptAsEntered(true);
        setParticipationAddressFromAutocomplete(false);
        setParticipationAddressVerifiedByPopup(false);
        // Store the current address as the original 'kept as entered' address
        setOriginalParticipationKeptAddress(
          `${participationAddress1}, ${participationCity}, ${participationState} ${participationZip}`,
        );
        formMethods.setValue('isAddressVerified', false, { shouldValidate: true });
      } else if (addressVerificationType === 'mailing') {
        setMailingAddressKeptAsEntered(true);
        setMailingAddressFromAutocomplete(false);
        setMailingAddressVerifiedByPopup(false);
        // Store the current address as the original 'kept as entered' address
        setOriginalMailingKeptAddress(
          `${mailingAddress1}, ${mailingCity}, ${mailingState} ${mailingZip}`,
        );
        formMethods.setValue('isMailingAddressVerified', false, { shouldValidate: true });
      }
    }

    // Only proceed with form submission if this was triggered during form submission
    if (!isManualVerification) {
      const submitEvent = new CustomEvent('proceedWithSubmission');
      window.dispatchEvent(submitEvent);
    }
  };

  const handleVendorOptionSelect = async (vendor: VendorOption) => {
    // Create an address object similar to what AddressAutocomplete provides
    const addressForValidation: AddressPrediction = {
      description: vendor.address,
      placeId: vendor.placeId,
      structuredFormatting: {
        mainText: vendor.address,
        secondaryText: '',
      },
    };

    // Handle the address selection based on verification type
    if (addressVerificationType === 'participation') {
      setParticipationAddressVerifiedByPopup(true);
      setParticipationAddressKeptAsEntered(false);
      await handleParticipationAddressSelect(addressForValidation);
    } else if (addressVerificationType === 'mailing') {
      setMailingAddressVerifiedByPopup(true);
      setMailingAddressKeptAsEntered(false);
      await handleMailingAddressSelect(addressForValidation);
    }
  };

  useEffect(() => {
    window.checkCustomAddresses = checkForCustomAddresses;
  }, [
    participationAddress1,
    mailingAddress1,
    participationAddressFromAutocomplete,
    mailingAddressFromAutocomplete,
    participationAddressVerifiedByPopup,
    mailingAddressVerifiedByPopup,
    participationAddressKeptAsEntered,
    mailingAddressKeptAsEntered,
    originalParticipationKeptAddress,
    originalMailingKeptAddress,
    checkMailingEnabled,
  ]);

  const handleCreateVendorAccount = (submitData: VendorAccountSubmitFormProps) => {
    setIsFormSubmitting(true);

    const normalizeUsPhoneNumber = (value?: string) => {
      const digits = (value || '').replace(/\D/g, '');
      if (!digits) return '';
      const tenDigits = digits.length >= 10 ? digits.slice(-10) : digits;
      return tenDigits.length === 10 ? `+1${tenDigits}` : value?.trim() || '';
    };

    const achNicknameValue = achNickname.trim() || 'Primary ACH';
    const checkNicknameValue = checkNickname.trim() || 'Primary Check';
    const checkPayeeNameValue = checkPayeeName.trim() || submitData.name;
    const checkRecipientNameValue = checkRecipientName.trim() || submitData.name;
    const normalizedMailingPhoneNumber = normalizeUsPhoneNumber(submitData.mailingPhoneNumber);

    const { defaultAchEnabled, defaultCheckMailingEnabled, isVendorConfirmationRequired } =
      submitData;
    const hasAchAccountForDefault =
      !!(submitData.accountType || '').trim() &&
      !!(submitData.routingNumber || '').trim() &&
      !!(submitData.accountNumber || '').trim();
    const hasCheckAddressForDefault =
      !!(submitData.mailingAddress1 || '').trim() &&
      !!(submitData.mailingCity || '').trim() &&
      !!(submitData.mailingState || '').trim() &&
      !!(submitData.mailingZip || '').trim();

    const defaultPaymentMethod: VendorAccountSubmitFormProps['defaultPaymentMethod'] =
      defaultAchEnabled && hasAchAccountForDefault
        ? 'ACH'
        : defaultCheckMailingEnabled && hasCheckAddressForDefault
          ? 'CHECK'
          : undefined;

    const achAccounts = submitData.achAccounts?.length
      ? submitData.achAccounts.map((account, index) => ({
        ...account,
        nickname: index === 0 ? achNicknameValue : account.nickname,
      }))
      : submitData.accountType && submitData.routingNumber
        ? [
          {
            nickname: achNicknameValue,
            accountType: submitData.accountType,
            routingNumber: submitData.routingNumber,
            accountNumber: submitData.accountNumber || '',
            isDefault: defaultPaymentMethod === 'ACH',
          },
        ]
        : [];

    const checkAccounts = submitData.checkAccounts?.length
      ? submitData.checkAccounts.map((account, index) => ({
        ...account,
        nickname: index === 0 ? checkNicknameValue : account.nickname,
        payeeName: index === 0 ? checkPayeeNameValue : account.payeeName,
        recipientName: index === 0 ? checkRecipientNameValue : account.recipientName,
      }))
      : submitData.mailingAddress1 && submitData.mailingCity && submitData.mailingState && submitData.mailingZip
        ? [
          {
            nickname: checkNicknameValue,
            payeeName: checkPayeeNameValue,
            recipientName: checkRecipientNameValue,
            address: submitData.mailingAddress1,
            addressLine2: submitData.mailingAddress2 || '',
            city: submitData.mailingCity,
            state: submitData.mailingState,
            zip: submitData.mailingZip,
            country: 'US',
            isDefault: defaultPaymentMethod === 'CHECK',
            mailingPhoneNumber: normalizedMailingPhoneNumber,
          },
        ]
        : [];

    const apiData: Parameters<typeof createVendorAccount>[0] = {
      name: submitData.name,
      ...(submitData.tin ? { tin: submitData.tin } : {}),
      participationAddress1: submitData.participationAddress1 || '',
      participationCity: submitData.participationCity || '',
      participationState: submitData.participationState || '',
      participationZip: submitData.participationZip || '',
      email: submitData.email || '',
      vendorType: submitData.vendorType || '',
      accountType: submitData.accountType || '',
      accountNumber: submitData.accountNumber || '',
      routingNumber: submitData.routingNumber || '',
      mailingAddress1: submitData.mailingAddress1 || '',
      mailingAddress2: submitData.mailingAddress2 || '',
      mailingCity: submitData.mailingCity || '',
      mailingState: submitData.mailingState || '',
      mailingZip: submitData.mailingZip || '',
      mailingPhoneNumber: normalizedMailingPhoneNumber,
      isVendorConfirmationRequired: isVendorConfirmationRequired || false,
      defaultPaymentMethod: defaultPaymentMethod,
      isAddressVerified: participationAddressKeptAsEntered
        ? false
        : participationAddressFromAutocomplete || participationAddressVerifiedByPopup,
      isMailingAddressVerified: mailingAddressKeptAsEntered
        ? false
        : mailingAddressFromAutocomplete || mailingAddressVerifiedByPopup,
      contactName: submitData.contactName || '',
      notes: submitData.notes || '',
      achAccounts,
      checkAccounts,
      ...(submitData.externalVendorId && { externalVendorId: submitData.externalVendorId }),
    };

    createVendorAccount(apiData, submitData.files ?? [])
      .then((response) => {
        const warningMessages = response.data?.warningMessages;
        if (warningMessages && warningMessages.length > 0) {
          setAlert((prev) => ({
            ...prev,
            message: warningMessages,
            type: 'warning',
          }));
        } else {
          setAlert((prev) => ({
            ...prev,
            message: 'Vendor account created successfully.',
            type: 'success',
          }));
        }
        onSuccess();
      })
      .catch((error: AxiosError<ErrorData>) => {
        setAlert((prev) => ({
          ...prev,
          message:
            error.response?.data.message || 'An error occurred while creating the vendor account.',
          type: 'error',
        }));
      })
      .finally(() => {
        setIsFormSubmitting(false);
      });
  };

  const onSuccess = () => {
    navigate('/vendors');
  };
  const onSubmitForm = (values: VendorAccountSubmitFormProps) => {
    const checkCustomAddresses = window.checkCustomAddresses;
    if (checkCustomAddresses && !checkCustomAddresses()) {
      setPendingSubmissionData(values);
      return;
    }
    proceedWithSubmission(values);
  };

  const proceedWithSubmission = (values: VendorAccountSubmitFormProps) => {
    handleCreateVendorAccount(values);
  };

  // Set address verification flags for validation inside useEffect to avoid infinite re-renders
  useEffect(() => {
    const vendorVerified =
      participationAddressFromAutocomplete ||
      participationAddressVerifiedByPopup ||
      participationAddressKeptAsEntered;
    const checkVerified = checkMailingEnabled
      ? mailingAddressFromAutocomplete ||
      mailingAddressVerifiedByPopup ||
      mailingAddressKeptAsEntered
      : false;
    if (formMethods.getValues('isAddressVerified') !== vendorVerified) {
      formMethods.setValue('isAddressVerified', vendorVerified);
    }
    if (formMethods.getValues('isMailingAddressVerified') !== checkVerified) {
      formMethods.setValue('isMailingAddressVerified', checkVerified);
    }
  }, [
    participationAddressFromAutocomplete,
    participationAddressVerifiedByPopup,
    participationAddressKeptAsEntered,
    mailingAddressFromAutocomplete,
    mailingAddressVerifiedByPopup,
    mailingAddressKeptAsEntered,
    checkMailingEnabled,
    formMethods,
  ]);

  useEffect(() => {
    const handleProceedWithSubmission = () => {
      if (pendingSubmissionData) {
        proceedWithSubmission(pendingSubmissionData);
        setPendingSubmissionData(null);
      }
    };

    window.addEventListener('proceedWithSubmission', handleProceedWithSubmission);
    return () => {
      window.removeEventListener('proceedWithSubmission', handleProceedWithSubmission);
    };
  }, [pendingSubmissionData]);

  const onCancel = () => {
    // Explicitly reset to default values
    setMailingAddressKey((prev) => prev + 1);
    setParticipationAddressKey((prev) => prev + 1);
    setAchNickname('');
    setCheckNickname('');
    setCheckPayeeName('');
    setCheckRecipientName('');
    setIsCheckPayeeNameManuallyEdited(false);
    setIsCheckRecipientNameManuallyEdited(false);
    setRoutingBankName(null);
    setRoutingLookupError(null);
    latestRoutingRef.current = '';
    reset(VENDOR_ACCOUNT_CREATE_DEFAULT_VALUES);
  };

  const saveDisabled = !isValid || !isDirty || isFormSubmitting;

  const collectInvalidControls = (
    errorNode: unknown,
    path = '',
  ): Array<{ path: string; message?: string; type?: string }> => {
    if (!errorNode || typeof errorNode !== 'object') return [];

    const node = errorNode as Record<string, unknown>;
    const hasErrorLeaf = typeof node.message === 'string' || typeof node.type === 'string';
    const current = hasErrorLeaf
      ? [
        {
          path,
          message: typeof node.message === 'string' ? node.message : undefined,
          type: typeof node.type === 'string' ? node.type : undefined,
        },
      ]
      : [];

    const children = Object.entries(node)
      .filter(([key]) => key !== 'message' && key !== 'type' && key !== 'ref')
      .flatMap(([key, value]) => collectInvalidControls(value, path ? `${path}.${key}` : key));

    return [...current, ...children];
  };

  const getValueByPath = (obj: unknown, path: string): unknown => {
    if (!obj || !path) return undefined;
    return path
      .split('.')
      .reduce<unknown>((acc, key) => (acc && typeof acc === 'object' ? (acc as Record<string, unknown>)[key] : undefined), obj);
  };

  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;

    const invalidControls = collectInvalidControls(errors).filter((entry) => entry.path);
    if (invalidControls.length === 0 && isValid) return;

    const valuesSnapshot = getValues();

    console.groupCollapsed('[VendorCreateForm] validation snapshot');
    console.log('formState', {
      isValid,
      isDirty,
      isSubmitting,
      isValidating,
      isFormSubmitting,
      submitCount,
      saveDisabled,
    });
    console.log('paymentMethodFlags', {
      achEnabled,
      checkMailingEnabled,
      defaultAchEnabled,
      defaultCheckMailingEnabled,
      sameAsVendorAddress,
    });
    console.log('addressVerificationFlags', {
      isAddressVerified: getValues('isAddressVerified'),
      isMailingAddressVerified: getValues('isMailingAddressVerified'),
      participationAddressFromAutocomplete,
      participationAddressVerifiedByPopup,
      participationAddressKeptAsEntered,
      mailingAddressFromAutocomplete,
      mailingAddressVerifiedByPopup,
      mailingAddressKeptAsEntered,
    });
    console.table(
      invalidControls.map((entry) => ({
        path: entry.path,
        type: entry.type,
        message: entry.message,
        value: getValueByPath(valuesSnapshot, entry.path),
      })),
    );
    console.log('errors', errors);
    console.log('dirtyFields', dirtyFields);
    console.log('touchedFields', touchedFields);
    console.groupEnd();
  }, [
    isValid,
    isDirty,
    isSubmitting,
    isValidating,
    isFormSubmitting,
    submitCount,
    saveDisabled,
    errors,
    dirtyFields,
    touchedFields,
    achEnabled,
    checkMailingEnabled,
    defaultAchEnabled,
    defaultCheckMailingEnabled,
    sameAsVendorAddress,
    participationAddressFromAutocomplete,
    participationAddressVerifiedByPopup,
    participationAddressKeptAsEntered,
    mailingAddressFromAutocomplete,
    mailingAddressVerifiedByPopup,
    mailingAddressKeptAsEntered,
    getValues,
  ]);

  // useEffect(() => {
  //   reset(VENDOR_ACCOUNT_CREATE_DEFAULT_VALUES);
  // }, [reset]);

  // useEffect(() => {
  //   const storedVendorData = sessionStorage.getItem('netsuiteVendorData');

  //   // Only reset if NOT coming from NetSuite
  //   if (!storedVendorData) {
  //     reset(VENDOR_ACCOUNT_CREATE_DEFAULT_VALUES);
  //   }
  // }, [reset]);

  // Effect to manage achEnabled and checkMailingEnabled based on default selection and field content
  useEffect(() => {
    // Helper function to check if a field has meaningful content
    const hasContent = (value: unknown) => {
      if (!value) return false;
      if (typeof value === 'object') {
        const obj = value as Record<string, unknown>;
        if (obj.id || obj.label) {
          const id = obj.id as string | undefined;
          const label = obj.label as string | undefined;
          return Boolean(id?.trim() || label?.trim());
        }
        return false;
      }
      return String(value).trim() !== '';
    };
    // Determine if ACH fields have any meaningful content
    const hasAchData =
      hasContent(achType) || hasContent(achAccountNumber) || hasContent(achRoutingNumber);

    // Determine if Check fields have any meaningful content
    const hasCheckData =
      hasContent(mailingPhoneNumber) ||
      hasContent(mailingAddress1) ||
      hasContent(mailingAddress2) ||
      hasContent(mailingCity) ||
      hasContent(mailingState) ||
      hasContent(mailingZip);

    // Set achEnabled: true if it's the default method OR if user has entered ACH data
    const shouldEnableAch = defaultAchEnabled || hasAchData;

    // Set checkMailingEnabled: true if it's the default method OR if user has entered Check data
    const shouldEnableCheckMailing = defaultCheckMailingEnabled || hasCheckData;

    // Only update if values have changed to avoid unnecessary re-renders
    if (achEnabled !== shouldEnableAch) {
      formMethods.setValue('achEnabled', shouldEnableAch);
    }

    if (checkMailingEnabled !== shouldEnableCheckMailing) {
      formMethods.setValue('checkMailingEnabled', shouldEnableCheckMailing);
    }
    trigger(['achEnabled', 'checkMailingEnabled']);
  }, [
    defaultAchEnabled,
    defaultCheckMailingEnabled,
    achType,
    achAccountNumber,
    achRoutingNumber,
    mailingPhoneNumber,
    mailingAddress1,
    mailingAddress2,
    mailingCity,
    mailingState,
    mailingZip,
    achEnabled,
    checkMailingEnabled,
    formMethods,
  ]);

  return (
    <>
      <Box className={styles.classes.tabsContainer} sx={{ mb: 3 }}></Box>
      <Box className={styles.classes.formWrapper}>
        <FormProvider {...formMethods}>
          <form className={styles.classes.vendorCreateForm}>
            <Box className={styles.classes.formContainer}>
              <Box
                display="flex"
                flexDirection="column"
                width="100%"
                maxWidth="470px"
                className={clsx(
                  styles.classes.rightColumnFieldTypography,
                  styles.classes.formFieldSystem,
                )}
              >
                <Typography className={styles.classes.fieldHeadline}>Vendor Details</Typography>

                <Box mt="16px">
                  {!isLoadingVendorConfig && !isVendorVisibilityRestricted && (
                    <FindVendorButton onButtonClick={handleSearchVendorClick} />
                  )}

                  {!isLoadingVendorConfig && findVendorConfig === 'netSuite' && (
                    <Box
                      sx={{
                        '& button': {
                          maxWidth: '225px'
                        },
                        '& p': {
                          textTransform: 'uppercase',
                          fontSize: '14px',
                          fontWeight: '500',
                        },
                      }}
                    >
                      <CommonButton
                        type="infoMain"
                        buttonText={`Find Vendor in ${vendorEntity}`}
                        // disabled={isSelectingNetSuiteVendor}
                        onButtonClick={() => setIsNetSuitePopupOpen(true)}
                      />
                    </Box>
                  )}
                </Box>

                <Box mt="24px">
                  <FormTextField
                    name="name"
                    label="Name *"
                    InputProps={{
                      placeholder: 'Enter vendor name',
                    }}
                  />
                </Box>

                <Box mt="24px">
                  <FormSelectField
                    name="vendorType"
                    label="Vendor type *"
                    options={VENDOR_TYPE_OPTIONS}
                    width="100%"
                  />
                </Box>

                <Box mt="24px">
                  <FormMaskedTextField
                    mask="99-9999999"
                    name="tin"
                    label="EIN/TIN"
                    InputProps={{
                      placeholder: 'Enter EIN/TIN',
                    }}
                  />
                </Box>

                <Box mt="24px">
                  <FormTextField
                    name="contactName"
                    label="Person to Contact *"
                    InputProps={{
                      placeholder: 'Enter contact name',
                    }}
                  />
                </Box>

                <Box mt="24px">
                  <FormTextField
                    name="email"
                    label="Email Address *"
                    InputProps={{
                      placeholder: 'email@example.com',
                    }}
                  />
                </Box>

                <Typography className={styles.classes.fieldHeadline} mt="32px">
                  Address
                </Typography>
                {/* Address Verification Status */}
                <Box mt="8px" display="flex" justifyContent="space-between">
                  {participationAddressFromAutocomplete || participationAddressVerifiedByPopup ? (
                    // Show green 'Verified' when address is from autocomplete or verified by popup
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: '#4CAF50',
                        fontSize: '14px',
                        fontWeight: 500,
                        fontFamily: 'Inter',
                        padding: '5px',
                      }}
                    >
                      <CheckCircleIcon sx={{ fontSize: '16px' }} />
                      <span>Admin side address verification completed</span>
                    </Box>
                  ) : participationAddressKeptAsEntered ? (
                    // Show warning icon with tooltip for 'keep as entered' addresses
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        color: '#FF9800',
                        fontSize: '14px',
                        fontWeight: 500,
                        fontFamily: 'Inter',
                        padding: '5px',
                      }}
                    >
                      <ErrorIcon sx={{ fontSize: '16px' }} />
                      <span>Vendor address not verified by USPS</span>
                    </Box>
                  ) : (
                    // Show orange 'Verify' link when address is manually typed and mandatory fields are filled
                    participationAddress1 &&
                    participationCity &&
                    participationState &&
                    participationZip &&
                    (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: '#FF9800',
                            fontSize: '14px',
                            fontWeight: 500,
                            fontFamily: 'Inter',
                            padding: '5px',
                          }}
                        >
                          <ErrorIcon sx={{ fontSize: '16px' }} />
                          <span>Vendor address not verified. Please verify</span>
                        </Box>
                        <Box onClick={handleVerifyAddress} className={styles.classes.verifyButton}>
                          <span>Verify</span>
                        </Box>
                      </>
                    )
                  )}
                </Box>
                <Box mt="16px">
                  <AddressAutocomplete
                    key={participationAddressKey} // Key to force remount when needed
                    name="participationAddress1"
                    label="Address Line 1"
                    optionsQuery={addressOptionsQuery}
                    mapQueryResults={mapAddressResults}
                    onValueChange={handleParticipationAddressSelect}
                    isDisableClearable={false}
                    addressType="Vendor Address"
                    InputProps={{
                      value: participationAddress1 || '',
                      placeholder: 'Enter address line 1',
                    }}
                  />
                </Box>

                <Box mt="24px">
                  <FormTextField
                    name="participationAddress2"
                    label="Address Line 2"
                    InputProps={{
                      placeholder: 'Enter address line 2 (optional)',
                    }}
                  />
                </Box>

                <Box mt="24px">
                  <FormTextField
                    name="participationCity"
                    label="City"
                    InputProps={{
                      placeholder: 'Enter city',
                    }}
                  />
                </Box>

                <Box
                  mt="24px"
                  display="flex"
                  gap="12px"
                  sx={{
                    flexDirection: {
                      xs: 'column',
                      sm: 'row',
                    },
                  }}
                >
                  <Box width="100%">
                    <FormSelectField
                      name="participationState"
                      label="State"
                      options={US_STATES_LIST}
                      width="100%"
                      shouldClearable={true}
                    />
                  </Box>
                  <Box width="100%">
                    <FormTextField
                      name="participationZip"
                      label="Zip"
                      InputProps={{
                        placeholder: 'Enter ZIP code',
                      }}
                    />
                  </Box>
                </Box>

                <Box mt="32px">
                  <FormCheckboxField
                    name="isVendorConfirmationRequired"
                    label="Need Vendor confirmation of Banking Details for provisioning."
                  />
                </Box>
              </Box>

              {/* Right Column - Payment Methods */}
              <Box
                display="flex"
                flexDirection="column"
                width="100%"
                maxWidth="470px"
                className={styles.classes.formFieldSystem}
              >
                <Box className={styles.classes.paymentMethodsPanel}>
                  <Box className={styles.classes.paymentMethodsHeader}>
                    <Typography className={styles.classes.fieldHeadline}>Payment Methods *</Typography>
                  </Box>

                  <Box className={styles.classes.paymentMethodsStack}>
                    {/* ACH Accordion */}
                    <Accordion
                      className={clsx(
                        styles.classes.accordion,
                        styles.classes.paymentAccordion,
                        styles.classes.paymentAccordionAch,
                      )}
                      expanded={achAccordionExpanded}
                      onChange={() => setAchAccordionExpanded(!achAccordionExpanded)}
                    >
                      <AccordionSummary
                        className={styles.classes.accordionSummary}
                        expandIcon={
                          achAccordionExpanded ? (
                            <ExpandMoreIcon sx={{ color: '#ffffff' }} />
                          ) : (
                            <ChevronRightIcon sx={{ color: '#ffffff' }} />
                          )
                        }
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          width="100%"
                        >
                          <Box className={styles.classes.paymentSummaryTitleWrap}>
                            <Typography className={styles.classes.paymentSummaryTitle}>ACH</Typography>
                            <Typography className={styles.classes.paymentSummaryCount}>
                              {achConfiguredCount === 0
                                ? 'Not configured'
                                : `${achConfiguredCount} account${achConfiguredCount === 1 ? '' : 's'}`}
                            </Typography>
                          </Box>
                          <Box
                            className={styles.classes.paymentDefaultWrap}
                            onClick={(e) => e.stopPropagation()} // Prevent accordion toggle for the entire box
                          >
                            <Typography className={styles.classes.paymentDefaultLabel}>Default Method</Typography>
                            <Radio
                              checked={defaultAchEnabled}
                              onChange={(e) => {
                                e.stopPropagation(); // Prevent accordion toggle
                                if (!hasAchDefaultCandidate) {
                                  setAlert((prev) => ({
                                    ...prev,
                                    message:
                                      'Complete ACH account details before setting ACH as the default payment method.',
                                    type: 'warning',
                                  }));
                                  return;
                                }
                                setValue('defaultAchEnabled', true);
                                setValue('defaultCheckMailingEnabled', false);
                                setAchAccordionExpanded(true);
                              }}
                              onClick={(e) => {
                                e.stopPropagation(); // Additional protection
                                setAchAccordionExpanded(true);
                              }}
                              className={styles.classes.paymentDefaultRadio}
                              disabled={!hasAchDefaultCandidate && !defaultAchEnabled}
                              size="small"
                            />
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails
                        className={clsx(styles.classes.accordionDetails, styles.classes.paymentAccordionDetails)}
                      >
                        <Box className={styles.classes.paymentFieldsStack}>
                          <Typography className={styles.classes.paymentSectionLabel}>ACH Accounts</Typography>
                          <TextField
                            id="vendor-create-ach-nickname"
                            fullWidth
                            variant="outlined"
                            className={styles.classes.matchingTextField}
                            label="Nickname *"
                            value={achNickname}
                            onChange={(event) => setAchNickname(event.target.value)}
                            InputProps={{
                              placeholder: 'e.g. Billing office',
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Tooltip title="Shown in the payment dropdown" arrow placement="top">
                                    <IconButton
                                      size="small"
                                      className={styles.classes.infoIconButton}
                                      tabIndex={-1}
                                      aria-label="Nickname helper"
                                    >
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </InputAdornment>
                              ),
                            }}
                          />
                          <FormSelectField
                            name="accountType"
                            label="Account Type *"
                            options={[
                              { id: 'Checking', label: 'Checking' },
                              { id: 'Savings', label: 'Savings' },
                            ]}
                            width="100%"
                          />
                          {isValidRoutingNumber(achRoutingNumber || '') &&
                            (routingBankName || routingLookupError) &&
                            (routingLookupError ? (
                              <Typography
                                sx={{
                                  color: 'warning.light',
                                  fontFamily: 'Inter',
                                  fontSize: '12px',
                                  lineHeight: '18px',
                                  marginTop: '2px',
                                  marginBottom: '2px',
                                }}
                              >
                                {ROUTING_LOOKUP_UNAVAILABLE_MESSAGE}
                              </Typography>
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  marginTop: '2px',
                                  marginBottom: '2px',
                                }}
                              >
                                <CheckCircleIcon sx={{ color: 'success.light', fontSize: '16px' }} />
                                <Typography
                                  sx={{
                                    color: 'success.light',
                                    fontFamily: 'Inter',
                                    fontSize: '12px',
                                    lineHeight: '18px',
                                  }}
                                >
                                  {routingBankName}
                                </Typography>
                              </Box>
                            ))}
                          <FormTextField
                            name="routingNumber"
                            label="Routing Number *"
                            inputProps={{ maxLength: 9 }}
                            handleChange={handleRoutingNumberChange}
                            onBlur={(value) => {
                              void handleRoutingNumberBlur(value);
                            }}
                            InputProps={{
                              placeholder: 'Enter routing number',
                            }}
                          />
                          <FormTextField
                            name="accountNumber"
                            label="Account Number *"
                            InputProps={{
                              placeholder: 'Enter account number',
                            }}
                          />
                        </Box>
                      </AccordionDetails>
                    </Accordion>

                    {/* CHECK Accordion */}
                    <Accordion
                      className={clsx(
                        styles.classes.accordion,
                        styles.classes.paymentAccordion,
                        styles.classes.paymentAccordionCheck,
                      )}
                      expanded={checkAccordionExpanded}
                      onChange={() => setCheckAccordionExpanded(!checkAccordionExpanded)}
                    >
                      <AccordionSummary
                        className={styles.classes.accordionSummary}
                        expandIcon={
                          checkAccordionExpanded ? (
                            <ExpandMoreIcon sx={{ color: '#ffffff' }} />
                          ) : (
                            <ChevronRightIcon sx={{ color: '#ffffff' }} />
                          )
                        }
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          width="100%"
                        >
                          <Box className={styles.classes.paymentSummaryTitleWrap}>
                            <Typography className={styles.classes.paymentSummaryTitle}>CHECK</Typography>
                            <Typography className={styles.classes.paymentSummaryCount}>
                              {checkConfiguredCount === 0
                                ? 'Not configured'
                                : `${checkConfiguredCount} account${checkConfiguredCount === 1 ? '' : 's'}`}
                            </Typography>
                          </Box>
                          <Box
                            className={styles.classes.paymentDefaultWrap}
                            onClick={(e) => e.stopPropagation()} // Prevent accordion toggle for the entire box
                          >
                            <Typography className={styles.classes.paymentDefaultLabel}>Default Method</Typography>
                            <Radio
                              checked={defaultCheckMailingEnabled}
                              onChange={(e) => {
                                e.stopPropagation(); // Prevent accordion toggle
                                if (!hasCheckDefaultCandidate) {
                                  setAlert((prev) => ({
                                    ...prev,
                                    message:
                                      'Complete check mailing address before setting CHECK as the default payment method.',
                                    type: 'warning',
                                  }));
                                  return;
                                }
                                setValue('defaultCheckMailingEnabled', true);
                                setValue('defaultAchEnabled', false);
                                setCheckAccordionExpanded(true);
                              }}
                              onClick={(e) => {
                                e.stopPropagation(); // Additional protection
                                setCheckAccordionExpanded(true);
                              }}
                              className={styles.classes.paymentDefaultRadio}
                              disabled={!hasCheckDefaultCandidate && !defaultCheckMailingEnabled}
                              size="small"
                            />
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails
                        className={clsx(styles.classes.accordionDetails, styles.classes.paymentAccordionDetails)}
                      >
                        <Box className={styles.classes.paymentFieldsStack}>
                          <Typography className={styles.classes.paymentSectionLabel}>Mailing Addresses</Typography>
                          <Box className={styles.classes.checkboxWrapper}>
                            <FormCheckboxField
                              name="sameAsVendorAddress"
                              label="Same as Vendor Address"
                              disabled={!hasParticipationAddress && !sameAsVendorAddress}
                              onChangeChecked={(checked) => {
                                setIsSyncingFromSameAsVendor(checked);
                                if (!checked) {
                                  setMailingAddressFromAutocomplete(false);
                                  setMailingAddressVerifiedByPopup(false);
                                  setMailingAddressKeptAsEntered(false);
                                  setOriginalMailingKeptAddress('');
                                }
                              }}
                            />
                          </Box>
                          <Box mt="8px" display="flex" justifyContent="space-between" alignItems="center" gap="8px">
                            {(mailingAddressFromAutocomplete || mailingAddressVerifiedByPopup) ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  color: '#4CAF50',
                                  fontSize: '14px',
                                  fontWeight: 500,
                                  fontFamily: 'Inter',
                                  padding: '5px',
                                }}
                              >
                                <CheckCircleIcon sx={{ fontSize: '16px' }} />
                                <span>Admin side address verification completed</span>
                              </Box>
                            ) : mailingAddressKeptAsEntered ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  color: '#FF9800',
                                  fontSize: '12px',
                                  fontWeight: 500,
                                  fontFamily: 'Inter',
                                }}
                              >
                                <ErrorIcon sx={{ fontSize: '14px' }} />
                                <span>Vendor address not verified by USPS</span>
                              </Box>
                            ) : (
                              mailingAddress1 &&
                              mailingCity &&
                              mailingState &&
                              mailingZip && (
                                <>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '4px',
                                      color: '#FF9800',
                                      fontSize: '12px',
                                      fontWeight: 500,
                                      fontFamily: 'Inter',
                                    }}
                                  >
                                    <ErrorIcon sx={{ fontSize: '14px' }} />
                                    <span>Vendor address not verified. Please verify</span>
                                  </Box>
                                  <Box onClick={handleVerifyMailingAddress} className={styles.classes.verifyButton}>
                                    <span>Verify</span>
                                  </Box>
                                </>
                              )
                            )}
                          </Box>
                          <TextField
                            id="vendor-create-check-nickname"
                            fullWidth
                            variant="outlined"
                            className={styles.classes.matchingTextField}
                            label="Nickname *"
                            value={checkNickname}
                            onChange={(event) => setCheckNickname(event.target.value)}
                            InputProps={{
                              placeholder: 'e.g. Billing office',
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Tooltip title="Shown in the payment dropdown" arrow placement="top">
                                    <IconButton
                                      size="small"
                                      className={styles.classes.infoIconButton}
                                      tabIndex={-1}
                                      aria-label="Nickname helper"
                                    >
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </InputAdornment>
                              ),
                            }}
                          />
                          <TextField
                            id="vendor-create-check-payee"
                            fullWidth
                            variant="outlined"
                            className={styles.classes.matchingTextField}
                            label="Payee name"
                            value={checkPayeeName}
                            onChange={(event) => {
                              setIsCheckPayeeNameManuallyEdited(true);
                              setCheckPayeeName(event.target.value);
                            }}
                            InputProps={{
                              placeholder: 'Defaults to vendor name',
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Tooltip
                                    title={'Prints on the check "Pay to the order of"'}
                                    arrow
                                    placement="top"
                                  >
                                    <IconButton
                                      size="small"
                                      className={styles.classes.infoIconButton}
                                      tabIndex={-1}
                                      aria-label="Payee name helper"
                                    >
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </InputAdornment>
                              ),
                            }}
                          />
                          <TextField
                            id="vendor-create-check-recipient"
                            fullWidth
                            variant="outlined"
                            className={styles.classes.matchingTextField}
                            label="Recipient name"
                            value={checkRecipientName}
                            onChange={(event) => {
                              setIsCheckRecipientNameManuallyEdited(true);
                              setCheckRecipientName(event.target.value);
                            }}
                            InputProps={{
                              placeholder: 'Defaults to payee',
                              endAdornment: (
                                <InputAdornment position="end">
                                  <Tooltip
                                    title="Who receives the mailed check, e.g. a collection agency"
                                    arrow
                                    placement="top"
                                  >
                                    <IconButton
                                      size="small"
                                      className={styles.classes.infoIconButton}
                                      tabIndex={-1}
                                      aria-label="Recipient name helper"
                                    >
                                      <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </InputAdornment>
                              ),
                            }}
                          />
                          <FormMaskedTextField
                            mask="+1-999-999-9999"
                            name="mailingPhoneNumber"
                            label="Mobile Number *"
                            InputProps={{
                              placeholder: '+1-555-123-4567',
                            }}
                          />
                          <AddressAutocomplete
                            key={`mailing-address-${mailingAddressKey}`}
                            name="mailingAddress1"
                            label="Address Line 1 *"
                            optionsQuery={addressOptionsQuery}
                            mapQueryResults={mapAddressResults}
                            onValueChange={handleMailingAddressSelect}
                            isDisableClearable={false}
                            addressType="Check Mailing Address"
                            InputProps={{
                              value: mailingAddress1 || '',
                              placeholder: 'Enter address line 1',
                            }}
                          />
                          <FormTextField
                            name="mailingAddress2"
                            label="Address Line 2"
                            InputProps={{
                              placeholder: 'Enter address line 2 (optional)',
                            }}
                          />
                          <FormTextField
                            name="mailingCity"
                            label="City *"
                            InputProps={{
                              placeholder: 'Enter city',
                            }}
                          />
                          <Box
                            display="flex"
                            gap="12px"
                            sx={{
                              flexDirection: {
                                xs: 'column',
                                sm: 'row',
                              },
                            }}
                          >
                            <Box width="100%">
                              <FormSelectField
                                name="mailingState"
                                label="State *"
                                options={US_STATES_LIST}
                                width="100%"
                                shouldClearable={true}
                              />
                            </Box>
                            <Box width="100%">
                              <FormTextField
                                name="mailingZip"
                                label="Zip *"
                                InputProps={{
                                  placeholder: 'Enter ZIP code',
                                }}
                              />
                            </Box>
                          </Box>
                        </Box>
                      </AccordionDetails>
                    </Accordion>

                    {/* V Accordion */}
                    <Accordion
                      className={clsx(
                        styles.classes.accordion,
                        styles.classes.paymentAccordion,
                        styles.classes.paymentAccordionUpcoming,
                      )}
                      expanded={false}
                      disabled
                      sx={{
                        '&.Mui-disabled': {
                          opacity: 1,
                          backgroundColor: 'transparent',
                        },
                        '& .MuiAccordionSummary-root.Mui-disabled': {
                          opacity: 1,
                          backgroundColor: 'transparent',
                        },
                        '& .MuiAccordionDetails-root': {
                          backgroundColor: 'transparent',
                        },
                      }}
                    >
                      <AccordionSummary
                        className={styles.classes.accordionSummary}
                        expandIcon={<ChevronRightIcon sx={{ color: '#8f93a6' }} />}
                      >
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent="space-between"
                          width="100%"
                        >
                          <Typography sx={{ color: '#8f93a6', fontWeight: 500, fontSize: '14px' }}>
                            V
                          </Typography>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap="8px"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Typography sx={{ color: '#8f93a6', fontSize: '12px' }}>
                              Default Method
                            </Typography>
                            <Radio
                              checked={false}
                              disabled
                              sx={{
                                color: '#8f93a6',
                                padding: '4px',
                              }}
                              size="small"
                            />
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails className={styles.classes.accordionDetails}>
                        <Typography sx={{ color: '#8f93a6', fontSize: '12px' }}>
                          Coming soon.
                        </Typography>
                      </AccordionDetails>
                    </Accordion>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box className={styles.classes.divider} />
            <Box display="flex" flexDirection="column" width="100%" mt={2} mb={2}>
              <Typography className={styles.classes.fieldHeadline}>Notes</Typography>
              <Box mt="16px">
                <FormTextField
                  name="notes"
                  label="Add notes about this vendor (optional)"
                  isMultiline
                  isDescription
                // disabled={!canEdit}
                />
              </Box>
            </Box>
            <Box display="flex" flexDirection="column" width="100%" mt={2} mb={2}>
              <Typography className={styles.classes.fieldHeadline}>Documents</Typography>
              <Box mt="16px">
                <FormMultiFileUploadField
                  name="files"
                  inputId="documents-upload-input"
                  acceptedTypes={[
                    'application/pdf',
                    'application/msword',
                    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                    'image/jpeg',
                    'image/jpg',
                    'image/png',
                  ]}
                  acceptedExtensionsText="PDF, DOC, DOCX, JPG, JPEG, PNG"
                  maxFileSize={10 * 1024 * 1024}
                />
              </Box>
            </Box>

            <Box display="flex" width="100%" justifyContent="center">
              <Box
                display="flex"
                mt="32px"
                maxWidth="416px"
                flexDirection="column"
                gap="16px"
                width="100%"
              >
                <FormSubmitButton
                  disabled={saveDisabled}
                  buttonIcon={<SaveIcon />}
                  buttonText="Save"
                  onClick={handleSubmit(onSubmitForm)}
                />
                <FormCancelButton onClick={onCancel} />
              </Box>
            </Box>
          </form>
        </FormProvider>
      </Box >

      <SearchVendorPopup
        open={searchVendorOpen}
        onClose={() => setSearchVendorOpen(false)}
        onSelectVendor={handleVendorSelect}
      />

      <FindNetSuiteVendorPopup
        open={isNetSuitePopupOpen}
        onClose={() => setIsNetSuitePopupOpen(false)}
        onFindVendorSelect={handleVendorSelectFromNetSuite}
        entityType="Vendor"
        vendorCreate={true}
      />

      <VendorConfirmationPopup
        open={vendorConfirmationOpen}
        addressType={
          addressVerificationType === 'participation' ? 'Vendor Address' : 'Check Mailing Address'
        }
        enteredAddress={
          addressVerificationType === 'participation'
            ? participationAddress1
              ? `${participationAddress1}${participationAddress2 ? ', ' + participationAddress2 : ''
              }, ${participationCity}, ${participationState} ${participationZip}`
              : ''
            : mailingAddress1
              ? `${mailingAddress1}${mailingAddress2 ? ', ' + mailingAddress2 : ''
              }, ${mailingCity}, ${mailingState} ${mailingZip}`
              : ''
        }
        onConfirm={handleVendorConfirmation}
        onCancel={() => setVendorConfirmationOpen(false)}
        onVendorSelect={handleVendorOptionSelect}
      />
    </>
  );
};

export default VendorCreateFormContainer;
